import React, { useState } from 'react';
import { XMarkIcon, EyeIcon, EyeSlashIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';

const AdminLoginModal = ({ isOpen, onClose, onAdminLogin }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    adminKey: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showAdminKey, setShowAdminKey] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError(''); // Clear error when user types
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // 관리자 인증 로직
      const adminCredentials = {
        username: 'admin',
        password: 'admin123!',
        adminKey: 'cbt-admin-2024'
      };

      if (
        formData.username === adminCredentials.username &&
        formData.password === adminCredentials.password &&
        formData.adminKey === adminCredentials.adminKey
      ) {
        // 관리자 정보 생성
        const adminUser = {
          id: 'admin_001',
          name: '시스템 관리자',
          email: 'admin@cbt.com',
          isAdmin: true,
          loginTime: new Date().toISOString()
        };

        localStorage.setItem('adminAuth', JSON.stringify(adminUser));
        onAdminLogin(adminUser);
        onClose();
        setFormData({ username: '', password: '', adminKey: '' });
      } else {
        setError('관리자 인증 정보가 올바르지 않습니다.');
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('관리자 로그인 오류:', error);
      }
      setError('로그인 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ username: '', password: '', adminKey: '' });
    setError('');
    setShowPassword(false);
    setShowAdminKey(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* 헤더 */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
              <ShieldCheckIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">관리자 로그인</h2>
              <p className="text-sm text-gray-500">시스템 관리자 전용 접속</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
          >
            <XMarkIcon className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* 폼 */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* 사용자명 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              관리자 ID
            </label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="관리자 아이디를 입력하세요"
              required
            />
          </div>

          {/* 비밀번호 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              비밀번호
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="비밀번호를 입력하세요"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? (
                  <EyeSlashIcon className="w-5 h-5" />
                ) : (
                  <EyeIcon className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          {/* 관리자 키 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              관리자 인증 키
            </label>
            <div className="relative">
              <input
                type={showAdminKey ? 'text' : 'password'}
                name="adminKey"
                value={formData.adminKey}
                onChange={handleInputChange}
                className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="관리자 인증 키를 입력하세요"
                required
              />
              <button
                type="button"
                onClick={() => setShowAdminKey(!showAdminKey)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showAdminKey ? (
                  <EyeSlashIcon className="w-5 h-5" />
                ) : (
                  <EyeIcon className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          {/* 로그인 버튼 */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-red-500 text-white py-3 px-4 rounded-lg font-semibold hover:bg-red-600 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                <span>로그인 중...</span>
              </div>
            ) : (
              '관리자 로그인'
            )}
          </button>

          {/* 안내 메시지 */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  ⚠️ 관리자 전용 접속
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>시스템 관리자만 접속할 수 있습니다.</p>
                  <p className="mt-1">관리자 인증이 필요합니다.</p>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminLoginModal;